import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import {
  borderRadius,
  borderStyle,
  display,
  maxHeight,
  maxWidth,
  size,
  borderColor as systemBorderColor,
  borderWidth as systemBorderWidth,
} from 'styled-system';

import { useLocalization } from '../../../../providers/LocaleProvider';
import { aspectRatio, boxSizing, cursor } from '../../../../theme/system-utilities';
import type { CarouselItemProps } from '../../../../types/carousel-item';
import { getEnterHandler } from '../../../../utils/keyboard';
import ResponsiveImage from '../../../ResponsiveImage';
import ThumbnailImageWrapper from '../../../ThumbnailImageWrapper';
import { SLIDE_BORDER_WIDTH, SLIDE_BORDER_WIDTH_ACTIVE } from './constants';
import type { ImageStyleProps } from './ImageBase';
import ImageBase, { imageBaseStyles } from './ImageBase';

interface ThumbnailCarouselSlideProps {
  data: CarouselItemProps;
  activeItem: number;
  index: number;
  onClick: (index: number) => void;
  withFixedSize?: boolean;
  ariaLabel?: string;
  baseId: string;
  count: number;
}

const StyledResponsiveImage = styled(ResponsiveImage)<ImageStyleProps>`
  ${size};
  ${display};
  ${cursor};
  ${boxSizing};
  ${borderStyle};
  ${maxWidth};
  ${borderRadius};
  ${maxHeight};
  ${aspectRatio};
  ${systemBorderColor};
  ${systemBorderWidth};
`;

interface ThumbnailAriaLabelParams {
  index: number;
  count: number;
  isVideo: boolean;
}

const useGetThumbnailAriaLabel = ({ index, count, isVideo }: ThumbnailAriaLabelParams) => {
  const { t } = useLocalization();
  const actionLabel = isVideo ? t('h24_play_video') : t('h24_select_photo');

  return `${actionLabel} ${index + 1} ${t('h24_of')} ${count}`;
};

const ThumbnailCarouselSlide = ({
  activeItem,
  index,
  data: { image: imageProps, icon },
  onClick,
  withFixedSize,
  ariaLabel,
  baseId,
  count,
}: ThumbnailCarouselSlideProps) => {
  const isActive = activeItem === index;
  const borderColor = isActive ? 'sys.neutral.border.default' : 'sys.neutral.border.weak';
  const borderWidth = isActive ? SLIDE_BORDER_WIDTH_ACTIVE : SLIDE_BORDER_WIDTH;
  const ImageComponent = icon || withFixedSize ? ImageBase : StyledResponsiveImage;
  const handleKeyDown = getEnterHandler(() => onClick(index));

  const { width, ...restImageProps } = imageProps;

  const wrapperRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isActive && wrapperRef.current) {
      wrapperRef.current?.focus();
    }
  }, [isActive]);
  const thumbnailAriaLabel = useGetThumbnailAriaLabel({ index, count, isVideo: icon === 'play' });

  return (
    <ThumbnailImageWrapper
      ref={wrapperRef}
      key={index}
      icon={icon}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="tab"
      {...(ariaLabel && { 'aria-label': ariaLabel })}
      onFocus={() => onClick(index)}
      aria-controls={`${baseId}-slide-${index}`}
      aria-label={thumbnailAriaLabel}
      aria-selected={activeItem === index}
    >
      <ImageComponent
        {...imageBaseStyles}
        {...restImageProps}
        {...(isActive ? { 'data-active-slide': true } : {})}
        data-testid={`thumbnail-carousel-slide-${index}`}
        borderWidth={borderWidth}
        borderColor={borderColor}
        alt=""
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onClick(index);
        }}
      />
    </ThumbnailImageWrapper>
  );
};

export default ThumbnailCarouselSlide;
